<script>
  // The Default Want List's Add a card input (spec "Wants" > Default Want
  // List only): the first row of the table, or the first tile in card view
  // (`compact`). As you type it suggests card names from the server
  // (search-api.suggestCardNames): names containing the text, most-played
  // first, or, only when none does, the closest spellings. Picking one
  // (click, or Up/Down then Enter) adds it. Enter before the suggestions are
  // in waits for them and adds the first; with none at all it adds the typed
  // text (the server canonicalizes it). On success the input clears
  // for the next card; on failure the text stays and the row says so.
  //
  // The input is never disabled: an add (the write, then the page's reload)
  // takes seconds on a phone, and a disabled input drops the keyboard while
  // looking no different, so the row read as frozen. It stays focused and
  // typeable, says "Adding <card>…" until the card lands, and a success
  // clears only the text that was added, never a next card typed meanwhile.
  // A lookup slower than ADD_CARD_SLOW_MS (an Enter waiting on one too) says
  // "Searching…"; the typing pause, the latest-answer-wins rule and the slow
  // mark are stores/typeahead.svelte.js.
  import { suggestCardNames } from '../../lib/services/search-api.js';
  import { portal } from '../../lib/actions/portal.js';
  import { comboboxKey, activeDescendant } from '../../lib/logic/combobox-keys.js';
  import { createTypeahead } from '../../lib/stores/typeahead.svelte.js';
  import { ADD_CARD_PAUSE_MS, ADD_CARD_SLOW_MS } from '../../lib/logic/config.js';

  /**
   * @type {{
   *   onAdd: (name: string) => Promise<boolean>,
   *   compact?: boolean,
   * }}
   */
  let { onAdd, compact = false } = $props();

  const NO_SUGGESTIONS = Promise.resolve({ names: [], closest: false });

  let q = $state('');
  let suggestions = $state(/** @type {string[]} */ ([]));
  let closest = $state(false);
  let active = $state(0);
  let open = $state(false);
  // The cards being added, oldest first (the status names the newest).
  let adding = $state(/** @type {string[]} */ ([]));
  let failed = $state('');
  let input = $state(/** @type {HTMLInputElement | null} */ (null));
  let popStyle = $state('');
  const listId = `add-card-${Math.random().toString(36).slice(2, 8)}`;

  // What is too short to suggest for is the server's call; no text, no ask.
  const typeahead = createTypeahead(
    (text) => (text.trim() ? suggestCardNames(text.trim()) : NO_SUGGESTIONS),
    {
      pauseMs: ADD_CARD_PAUSE_MS,
      slowMs: ADD_CARD_SLOW_MS,
      onResult(res) {
        suggestions = res.names;
        closest = res.closest;
        active = 0;
        place();
        open = suggestions.length > 0;
      },
    }
  );
  // Nothing pending fires once the row is gone.
  $effect(() => typeahead.cancel);

  const status = $derived(
    adding.length ? `Adding ${adding.at(-1)}…` : typeahead.state.slow ? 'Searching…' : ''
  );

  /** The popup sits under the input, in <body> (the table clips). */
  function place() {
    if (!input) return;
    const r = input.getBoundingClientRect();
    const width = Math.max(r.width, 260);
    const left = Math.max(8, Math.min(r.left, document.documentElement.clientWidth - width - 8));
    popStyle = `left:${left}px;top:${r.bottom + 4}px;width:${width}px`;
  }

  function close() {
    open = false;
  }

  function onInput() {
    failed = '';
    typeahead.schedule(q);
  }

  /** Enter: the highlighted suggestion, once the text's suggestions are in;
   *  the typed text only when there are none. */
  async function pick() {
    await typeahead.settle(q);
    add(open && suggestions[active] ? suggestions[active] : q);
  }

  /** @param {string} name */
  async function add(name) {
    const card = name.trim();
    if (!card || adding.includes(card)) return;
    const typed = q;
    typeahead.cancel(); // nothing still in flight reopens the list
    close();
    failed = '';
    adding = [...adding, card];
    input?.focus(); // ready for the next card while this one lands
    const ok = await onAdd(card);
    adding = adding.filter((c) => c !== card);
    if (!ok) {
      failed = card;
    } else if (q === typed) {
      // Only the text that was added; a next card typed meanwhile stays.
      q = '';
      suggestions = [];
    }
  }

  /** @param {KeyboardEvent} e */
  function onKeydown(e) {
    const hit = comboboxKey(e.key, { open, active, count: suggestions.length });
    if (!hit) return;
    e.preventDefault();
    if (hit.stop) e.stopPropagation();
    const { action } = hit;
    if (action.type === 'open') open = true;
    else if (action.type === 'move') active = action.active;
    else if (action.type === 'pick') pick();
    else close();
  }
</script>

<svelte:window onresize={() => open && place()} onscrollcapture={() => open && place()} />

<div class="add" class:compact>
  <input
    bind:this={input}
    bind:value={q}
    oninput={onInput}
    onkeydown={onKeydown}
    onfocus={() => {
      if (suggestions.length) {
        place();
        open = true;
      }
    }}
    onblur={close}
    placeholder="Add a card"
    aria-label="Add a card"
    role="combobox"
    aria-expanded={open}
    aria-controls={listId}
    aria-autocomplete="list"
    aria-activedescendant={activeDescendant(listId, open, active)}
    autocomplete="off"
  />
  <span class="busy" role="status">{status}</span>
  {#if failed}<span class="failed" role="alert">Couldn't add {failed}</span>{/if}
</div>

{#if open && suggestions.length}
  <ul
    id={listId}
    class="suggest"
    role="listbox"
    aria-label="Card names"
    style={popStyle}
    use:portal
  >
    {#each suggestions as s, i (s)}
      <!-- Keyboard picks through the input (combobox); the list never takes focus. -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <li
        id="{listId}-{i}"
        class="opt"
        class:active={i === active}
        role="option"
        aria-selected={i === active}
        onpointerdown={(e) => e.preventDefault()}
        onclick={() => add(s)}
        onpointerenter={() => (active = i)}
      >
        {s}
      </li>
    {/each}
    {#if closest}<li class="note" role="presentation">Closest spellings</li>{/if}
  </ul>
{/if}

<style>
  .add {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  input {
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 12px;
    border: 1.5px dashed var(--color-border);
    border-radius: 10px;
    background: transparent;
    font: inherit;
    font-size: 16px;
    color: var(--color-text);
  }
  input:focus {
    outline: none;
    border-style: solid;
    border-color: var(--color-accent-dark);
    background: var(--color-bg-surface);
  }
  /* In the add tile the input is the tile's label, like "New project". */
  .compact {
    width: 100%;
    flex-direction: column;
    gap: 4px;
  }
  .compact input,
  .compact input:focus {
    width: 100%;
    height: 30px;
    padding: 0 4px;
    border: none;
    background: transparent;
    text-align: center;
    font-weight: 700;
  }
  .compact input::placeholder {
    color: var(--color-text-secondary);
  }
  .busy,
  .failed {
    max-width: 50%;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .busy {
    color: var(--color-text-secondary);
  }
  /* Always in the DOM (a live region must exist before it speaks); empty, it
     takes no room. */
  .busy:empty {
    display: none;
  }
  .compact .busy,
  .compact .failed {
    max-width: 100%;
  }
  .failed {
    color: var(--color-error);
  }
  .suggest {
    position: fixed;
    z-index: 2000;
    margin: 0;
    padding: 4px;
    list-style: none;
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-bg-surface);
    box-shadow: 0 10px 30px rgba(44, 36, 25, 0.2);
  }
  .opt {
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 14px;
    color: var(--color-text);
    cursor: pointer;
  }
  .opt.active {
    background: var(--color-accent-bg);
  }
  .note {
    padding: 6px 10px 4px;
    font-size: 11px;
    color: var(--color-text-tertiary);
  }
</style>
